import type { WhitespaceGridCell, WhitespaceGridRow, WhitespaceItem } from 'hooks/useWhitespaceAnalysisGridData';

import { buildWhitespaceCellId } from './whitespaceMixpanel.helpers';
import {
    buildWhitespaceExcelFormat,
    EMPTY_VALUE,
    formatYAxisRange,
    getCellValueSales,
    getCrossSectionValue,
    getDecimalPlacesForSuffix,
    getFormattedKpiValue,
    getFormattedValueSales,
    getRawCellValueSales,
    getRawKpiValue,
    getRawValueSalesGrowthVsYa,
    getRawValueSalesShareInCell,
    getValueSalesGrowthVsYa,
    getValueSalesShareInCell,
    getYAxisRangeRawValue,
    toWhitespaceExportCellValue,
} from './whitespaceExportValueFormatters';

export type WhitespaceExportScope = 'current_view' | 'opportunity_cells_only' | 'all_filtered_cells';
export type WhitespaceExportFormat = 'xlsx' | 'csv';

type ExportValue = string | number;

type WhitespaceExportContext = {
    xAxisValue: string;
    yAxisLabel: string;
    yAxisBucket: string;
    crossSection: string;
    selectedYAxisKpiId?: string;
    cell: WhitespaceGridCell;
    item?: WhitespaceItem;
};

type WhitespaceExportColumnKey =
    | 'cell_id'
    | 'x_axis_value'
    | 'y_axis_range'
    | 'cross_section_value'
    | 'entity_type'
    | 'y_axis_kpi'
    | 'value_sales'
    | 'cell_value_sales'
    | 'value_sales_share_in_cell'
    | 'value_sales_growth_vs_ya';

export const WHITESPACE_EXPORT_COLUMN_KEYS: WhitespaceExportColumnKey[] = [
    'cell_id',
    'x_axis_value',
    'y_axis_range',
    'cross_section_value',
    'entity_type',
    'y_axis_kpi',
    'value_sales',
    'cell_value_sales',
    'value_sales_share_in_cell',
    'value_sales_growth_vs_ya',
];

type ColumnFormatHints = {
    yAxisKpiPrefix: string;
    yAxisKpiSuffix: string;
    valueSalesPrefix: string;
    cellValueSalesPrefix: string;
};

const EMPTY_FORMAT_HINTS: ColumnFormatHints = {
    yAxisKpiPrefix: '',
    yAxisKpiSuffix: '',
    valueSalesPrefix: '',
    cellValueSalesPrefix: '',
};

const getExportHeader = ({
    columnKey,
    xAxisLabel,
    yAxisLabel,
    crossSection,
}: {
    columnKey: WhitespaceExportColumnKey;
    xAxisLabel: string;
    yAxisLabel: string;
    crossSection: string;
}) => {
    switch (columnKey) {
        case 'cell_id':
            return 'Cell ID';
        case 'x_axis_value':
            return xAxisLabel || 'X-Axis Value';
        case 'y_axis_range':
            return yAxisLabel ? `${yAxisLabel} Range` : 'Y-Axis KPI Range';
        case 'cross_section_value':
            return crossSection || 'Cross Section';
        case 'entity_type':
            return 'Entity Type';
        case 'y_axis_kpi':
            return yAxisLabel || 'Y-Axis KPI';
        case 'value_sales':
            return 'Value Sales';
        case 'cell_value_sales':
            return 'Cell Value Sales';
        case 'value_sales_share_in_cell':
            return 'Value Sales Share % in Cell';
        case 'value_sales_growth_vs_ya':
            return 'Value Sales Growth % (vs YA)';
        default:
            return columnKey;
    }
};

type ExportValueResolver = (context: WhitespaceExportContext) => ExportValue;

const EXPORT_VALUE_RESOLVERS: Record<WhitespaceExportColumnKey, ExportValueResolver> = {
    cell_id: ({ xAxisValue, yAxisLabel, yAxisBucket }) =>
        buildWhitespaceCellId(xAxisValue, yAxisLabel || 'Y-Axis KPI', yAxisBucket),
    x_axis_value: ({ xAxisValue }) => xAxisValue || EMPTY_VALUE,
    y_axis_range: ({ yAxisBucket }) => getYAxisRangeRawValue(yAxisBucket),
    cross_section_value: ({ item, crossSection }) => getCrossSectionValue(item, crossSection),
    entity_type: ({ item }) => (item?.is_own_attribute ? 'Own' : 'Competitor'),
    y_axis_kpi: ({ item, selectedYAxisKpiId }) => {
        const kpiIds = selectedYAxisKpiId ? [selectedYAxisKpiId] : [];
        return toWhitespaceExportCellValue(
            getRawKpiValue(item, kpiIds),
            getFormattedKpiValue(item, kpiIds),
        );
    },
    value_sales: ({ item }) =>
        toWhitespaceExportCellValue(
            getRawKpiValue(item, ['value_sales', 'activate_value_sales']),
            getFormattedValueSales(item),
        ),
    cell_value_sales: ({ cell }) =>
        toWhitespaceExportCellValue(getRawCellValueSales(cell), getCellValueSales(cell)),
    value_sales_share_in_cell: ({ item, cell }) =>
        toWhitespaceExportCellValue(
            getRawValueSalesShareInCell(item, cell),
            getValueSalesShareInCell(item, cell),
        ),
    value_sales_growth_vs_ya: ({ item }) =>
        toWhitespaceExportCellValue(
            getRawValueSalesGrowthVsYa(item),
            getValueSalesGrowthVsYa(item),
        ),
};

const getExportValue = (columnKey: WhitespaceExportColumnKey, context: WhitespaceExportContext): ExportValue => {
    const resolver = EXPORT_VALUE_RESOLVERS[columnKey];
    return resolver ? resolver(context) : EMPTY_VALUE;
};

const updateColumnFormatHints = (context: WhitespaceExportContext, hints: ColumnFormatHints) => {
    if (!hints.yAxisKpiPrefix || !hints.yAxisKpiSuffix) {
        const yAxisKpi = context.item?.kpis?.find(
            (kpi) => (kpi.kpi_id ?? '').trim().toLowerCase() === (context.selectedYAxisKpiId ?? '').trim().toLowerCase(),
        );
        if (yAxisKpi) {
            if (!hints.yAxisKpiPrefix && yAxisKpi.valuePrefix) hints.yAxisKpiPrefix = yAxisKpi.valuePrefix;
            if (!hints.yAxisKpiSuffix && yAxisKpi.valueSuffix) hints.yAxisKpiSuffix = yAxisKpi.valueSuffix;
        }
    }

    if (!hints.valueSalesPrefix) {
        const valueSalesKpi = context.item?.kpis?.find((kpi) => {
            const normalized = (kpi.kpi_id ?? '').trim().toLowerCase();
            return normalized === 'value_sales' || normalized === 'activate_value_sales';
        });
        if (valueSalesKpi?.valuePrefix) {
            hints.valueSalesPrefix = valueSalesKpi.valuePrefix;
        }
    }

    if (!hints.cellValueSalesPrefix && context.cell.valuePrefix) {
        hints.cellValueSalesPrefix = context.cell.valuePrefix;
    }
};

const getYAxisKpiDecimalPlaces = (suffix: string, kpiId?: string) => {
    if (getDecimalPlacesForSuffix(suffix) === 0) return 0;
    const normalized = (kpiId ?? '').trim().toLowerCase();
    if (normalized === 'count' || normalized.includes('ounce')) return 0;
    return 2;
};

const buildWhitespaceColumnFormats = (
    hints: ColumnFormatHints,
    selectedYAxisKpiId?: string,
): Array<string | null> => [
    null,
    null,
    null,
    null,
    null,
    buildWhitespaceExcelFormat({
        prefix: hints.yAxisKpiPrefix,
        suffix: hints.yAxisKpiSuffix,
        decimalPlaces: getYAxisKpiDecimalPlaces(hints.yAxisKpiSuffix, selectedYAxisKpiId),
    }),
    buildWhitespaceExcelFormat({ prefix: hints.valueSalesPrefix, decimalPlaces: 2, useGrouping: false }),
    buildWhitespaceExcelFormat({ prefix: hints.cellValueSalesPrefix, decimalPlaces: 2, useGrouping: false }),
    buildWhitespaceExcelFormat({ isPercent: true, decimalPlaces: 2 }),
    buildWhitespaceExcelFormat({ isPercent: true, decimalPlaces: 2 }),
];

export type WhitespaceNormalizedExportResult = {
    headers: string[];
    rows: Array<Array<ExportValue>>;
    displayRows: Array<Array<ExportValue>>;
    columnFormats: Array<string | null>;
    dataRowCount: number;
};

export const buildWhitespaceExportRows = ({
    gridRows,
    columnHeaders,
    xAxisLabel,
    yAxisLabel,
    crossSection,
    scope,
    selectedYAxisKpiId,
}: {
    gridRows: WhitespaceGridRow[];
    columnHeaders: string[];
    xAxisLabel?: string;
    yAxisLabel?: string;
    crossSection: string;
    scope: WhitespaceExportScope;
    selectedYAxisKpiId?: string;
}): WhitespaceNormalizedExportResult => {
    const resolvedXAxisLabel = xAxisLabel || 'X-Axis Value';
    const resolvedYAxisLabel = yAxisLabel || selectedYAxisKpiId || 'Y-Axis KPI';
    const headers = WHITESPACE_EXPORT_COLUMN_KEYS.map((columnKey) =>
        getExportHeader({
            columnKey,
            xAxisLabel: resolvedXAxisLabel,
            yAxisLabel: resolvedYAxisLabel,
            crossSection,
        }),
    );

    const rows: Array<Array<ExportValue>> = [];
    const displayRows: Array<Array<ExportValue>> = [];
    const formatHints: ColumnFormatHints = { ...EMPTY_FORMAT_HINTS };

    // Preserve UI ordering: all Y-axis rows for each X-axis column before advancing.
    columnHeaders.forEach((columnHeader) => {
        gridRows.forEach((gridRow) => {
            const cell = gridRow.cells[columnHeader];
            if (!cell || (scope === 'opportunity_cells_only' && !cell.isWhitespace)) return;

            const items = cell.items?.length ? cell.items : [undefined];
            items.forEach((item) => {
                const context: WhitespaceExportContext = {
                    xAxisValue: columnHeader,
                    yAxisLabel: resolvedYAxisLabel,
                    yAxisBucket: gridRow.bucketLabel,
                    crossSection,
                    selectedYAxisKpiId,
                    cell,
                    item,
                };
                updateColumnFormatHints(context, formatHints);
                const row = WHITESPACE_EXPORT_COLUMN_KEYS.map((columnKey) => getExportValue(columnKey, context));
                const displayRow = WHITESPACE_EXPORT_COLUMN_KEYS.map((columnKey, columnIndex) => {
                    if (columnKey === 'y_axis_range') {
                        return formatYAxisRange({
                            bucketLabel: gridRow.bucketLabel,
                            item,
                            selectedYAxisKpiId,
                        });
                    }
                    return row[columnIndex];
                });
                rows.push(row);
                displayRows.push(displayRow);
            });
        });
    });

    return {
        headers,
        rows,
        displayRows,
        columnFormats: buildWhitespaceColumnFormats(formatHints, selectedYAxisKpiId),
        dataRowCount: rows.length,
    };
};

const Y_AXIS_RANGE_COLUMN_INDEX = WHITESPACE_EXPORT_COLUMN_KEYS.indexOf('y_axis_range');

export const buildWhitespaceXlsxDataRows = (
    rows: Array<Array<ExportValue>>,
    displayRows: Array<Array<ExportValue>>,
): Array<Array<ExportValue>> =>
    rows.map((row, rowIndex) =>
        row.map((cell, columnIndex) =>
            columnIndex === Y_AXIS_RANGE_COLUMN_INDEX ? displayRows[rowIndex][columnIndex] : cell,
        ),
    );
